import { network } from '@round/utils';
import { skipToken, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAllPostPaymentDetails } from '@round/creatorbase-helpers/api-hooks';

type Params = {
    campaignId?: number;
    paymentRequestId?: number | null | undefined;
};

export function useCurrentCampaignPaymentDetails(params: Params, config?: { enabled: boolean }) {
    const queryClient = useQueryClient();

    const {
        data: campaignPaymentDetails,
        isFetching: areCampaignPaymentDetailsFetching,
        isLoading: isCampaignPaymentDetailsLoading,
        isSuccess: campaignPaymentDetailsIsSuccess,
        error: campaignPaymentDetailsError,
        invalidate: invalidateCampaignPaymentDetails,
    } = useAllPostPaymentDetails(
        { campaign_id: params.campaignId },
        {
            ...config,
            enabled: !!params.campaignId && config?.enabled !== false,
            select: (data) =>
                data.find((detail) => detail.payment_request_status === 'UNREQUESTED') ||
                data.find((detail) => detail.payment_request_id === params.paymentRequestId) ||
                null,
        }
    );

    const fileKey = ['current-campaign-payment-details-file', campaignPaymentDetails?.file_upload];
    const fileQuery = useQuery({
        queryKey: fileKey,
        queryFn: !campaignPaymentDetails?.file_upload
            ? skipToken
            : async () => {
                  const response = await network.fetchFile(campaignPaymentDetails?.file_upload!);
                  if (response instanceof File) {
                      return response;
                  }

                  throw new Error('Could not fetch file');
              },
        enabled: !!campaignPaymentDetails?.file_upload,
    });

    const invalidateFile = () => {
        queryClient.resetQueries({ queryKey: fileKey });
    };

    return {
        campaignPaymentDetails,
        file: fileQuery.data || null,
        hasFileError: fileQuery.error,
        hasError: !!campaignPaymentDetailsError,
        isFetching: areCampaignPaymentDetailsFetching || fileQuery.isFetching,
        isLoading: isCampaignPaymentDetailsLoading,
        isSuccess: campaignPaymentDetailsIsSuccess,
        invalidate: () => {
            invalidateCampaignPaymentDetails();
            invalidateFile();
        },
    };
}
